import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { CRow, CCol } from '@coreui/react-pro';
import { Modal } from 'react-bootstrap';
import { CXButton } from '../../../components/CXForm';
import MultiSelectClearDropdown, { MultiSelectOption } from '../../../components/MultiSelectClearDropdown';

export type UpdateAssigneePayload = {
    assigneeLead?: string;
    assigneeGroup?: string[];
};

export interface UpdateAssigneeModalProps {
    show: boolean;
    onHide: () => void;
    onConfirm: (payload: UpdateAssigneePayload) => void;

    availableLeads: string[];
    availableGroups: string[];
    initialLead?: string;
    initialGroups?: string[];
}

type AssigneeFormState = {
    assigneeLead: MultiSelectOption[];
    assigneeGroup: MultiSelectOption[];
};

const UpdateAssigneeModal: React.FC<UpdateAssigneeModalProps> = ({
    show,
    onHide,
    onConfirm,
    availableLeads,
    availableGroups,
}) => {
    const [assigneeForm, setAssigneeForm] = useState<AssigneeFormState>({
        assigneeLead: [],
        assigneeGroup: [],
    });

    const [leadResetKey, setLeadResetKey] = useState(0);
    const [groupResetKey, setGroupResetKey] = useState(0);
    const [error, setError] = useState('');

    const leadOptions: MultiSelectOption[] = useMemo(
        () =>
            Array.from(new Set(availableLeads || [])).map((v) => ({
                value: v,
                text: v,
            })),
        [availableLeads],
    );

    const groupOptions: MultiSelectOption[] = useMemo(
        () =>
            Array.from(new Set(availableGroups || [])).map((v) => ({
                value: v,
                text: v,
            })),
        [availableGroups],
    );

    useEffect(() => {
        if (!show) return;

        setAssigneeForm({ assigneeLead: [], assigneeGroup: [] });
        setError('');
        setLeadResetKey((k) => k + 1);
        setGroupResetKey((k) => k + 1);
    }, [show]);

    const onChangeAssigneeField = useCallback(
        (
            value: MultiSelectOption[],
            field: string,
            state: any,
            setState: (next: any) => void,
        ) => {
            const currentState = state as AssigneeFormState;

            let normalizedValue = value;

            if (field === 'assigneeLead' && value.length > 1) {
                normalizedValue = [value[value.length - 1]];
            }

            const next: AssigneeFormState = {
                ...currentState,
                [field]: normalizedValue,
            } as AssigneeFormState;

            if (field === 'assigneeLead' && normalizedValue.length > 0) {
                if (currentState.assigneeGroup.length > 0) {
                    next.assigneeGroup = [];
                    setGroupResetKey((k) => k + 1);
                }
            }

            if (field === 'assigneeGroup' && normalizedValue.length > 0) {
                if (currentState.assigneeLead.length > 0) {
                    next.assigneeLead = [];
                    setLeadResetKey((k) => k + 1);
                }
            }

            setError('');
            setState(next);
        },
        [],
    );

    const handleConfirm = useCallback(() => {
        const leadValue = assigneeForm.assigneeLead?.[0]?.value?.trim();
        const groupValues = assigneeForm.assigneeGroup.map((o) => String(o.value).trim()).filter(Boolean);

        if (!leadValue && groupValues.length === 0) {
            setError('Please select either assignee lead or assignee group.');
            return;
        }

        if (leadValue && groupValues.length > 0) {
            setError('Please update either assignee lead or assignee group, not both.');
            return;
        }

        onConfirm({
            assigneeLead: leadValue || undefined,
            assigneeGroup: groupValues.length ? groupValues : undefined,
        });
    }, [assigneeForm, onConfirm]);

    const handleHide = useCallback(() => {
        onHide();
    }, [onHide]);

    return (
        <Modal
            className="updateAssigneeModal"
            show={show}
            onHide={handleHide}
            size="lg"
            autoFocus={false}
        >
            <Modal.Header className="modal-header" closeButton>
                <Modal.Title>Update assignee</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <CRow style={{ marginBottom: 24 }}>
                    <CCol xs={3} style={{ paddingTop: 16 }}>
                        <span style={{ fontWeight: 400, fontSize: 16, lineHeight: '24px', color: '#4C4C4C' }}>
                            Assignee lead
                        </span>
                    </CCol>
                    <CCol xs={9} style={{ paddingTop: 16 }}>
                        <MultiSelectClearDropdown
                            key={`lead-${leadResetKey}`}
                            resetKey={leadResetKey}
                            values={assigneeForm.assigneeLead}
                            onChange={setAssigneeForm}
                            defaultOptions={leadOptions}
                            onChangeField={onChangeAssigneeField}
                            field="assigneeLead"
                            state={assigneeForm}
                            zIndex={1410}
                            placeholder="Assignee lead"
                            selectAll={false}
                        />
                    </CCol>
                </CRow>

                <CRow>
                    <CCol xs={3} style={{ paddingTop: 36 }}>
                        <span style={{ fontWeight: 400, fontSize: 16, lineHeight: '24px', color: '#4C4C4C' }}>
                            Assignee group
                        </span>
                    </CCol>
                    <CCol xs={9} style={{ paddingTop: 36 }}>
                        <MultiSelectClearDropdown
                            key={`group-${groupResetKey}`}
                            resetKey={groupResetKey}
                            values={assigneeForm.assigneeGroup}
                            onChange={setAssigneeForm}
                            defaultOptions={groupOptions}
                            onChangeField={onChangeAssigneeField}
                            field="assigneeGroup"
                            state={assigneeForm}
                            zIndex={1409}
                            placeholder="Assignee group"
                            selectAll={false}
                        />
                    </CCol>
                </CRow>

                {error && (
                    <div style={{ marginTop: 16, fontSize: 12, lineHeight: '16px', color: '#C41E3A' }}>
                        {error}
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer className="modal-footer">
                <div style={{ width: 200 }}>
                    <CXButton outline text="Cancel" onClick={handleHide} fontSize="16px" width="100%" />
                </div>
                <div style={{ width: 200 }}>
                    <CXButton text="Update" onClick={handleConfirm} fontSize="16px" width="100%" />
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default UpdateAssigneeModal;
